// ─────────────────────────────────────────────────────────────────────────────
//  THIS IS THE FILE YOU EDIT.  One entry per hanging cutout.
//
//  id        unique slug (also seeds the hand-cut edge wobble and sway phase)
//  title     shown in the floating label
//  kicker    small line above the title (role · year · client…)
//  summary   one or two sentences
//  href      where a click goes (passed to onSelect; use your router's path)
//  frames    flipbook images, played in order while hovered. Frame 0 is the
//            resting image. Any number of frames, any size: each image is
//            centre-cropped ("cover") to its cutout, never squashed.
//
//  Placement and silhouette come from fromSketch(), which turns a traced outline
//  into 3D numbers (pos, size, shape):
//
//    ...fromSketch(outline, z, { threads })
//
//    outline   corners of the paper, in pixels on the 2000×1500 sketch (y down)
//    z         depth: smaller = farther back (try -4 … 0.6)
//    threads   where the strings are tied: [x, y] in the same pixel space,
//              placed inside the outline near its top edge
// ─────────────────────────────────────────────────────────────────────────────
import { fromSketch } from './shapes'

const scaleSketch = (points, scale = 0.82) => {
  const xs = points.map(([x]) => x)
  const ys = points.map(([, y]) => y)
  const cx = (Math.min(...xs) + Math.max(...xs)) / 2
  const cy = (Math.min(...ys) + Math.max(...ys)) / 2

  return points.map(([x, y]) => [cx + (x - cx) * scale, cy + (y - cy) * scale])
}

// If your site is not served from the domain root, prefix with import.meta.env.BASE_URL.
const interactiveRoot = '/project_images_compressed/interactive'
const framesFor = (project, prefix, start, end) =>
  Array.from({ length: end - start + 1 }, (_, index) => `${interactiveRoot}/${project}/${prefix}${start + index}.jpg`)

const projectFrames = {
  city: framesFor('city-unwrapped', 'city', 1, 18),
  sand: framesFor('sand-remembers', 'sea', 1, 20),
  chum: framesFor('my-little-chum', 'chummy', 1, 40),
  plane: ['/project_images_compressed/animation/toy-plane/toy-plane.jpg'],
  code: framesFor('cell-migration', 'cell', 1, 18),
  design: framesFor('lavender', 'pink', 1, 19),
  fish: framesFor('and-they-danced', 'fish', 1, 13),
  will: framesFor('will-fridays', 'will', 1, 9),
}

export const projects = [
  {
    id: 'city-unwrapped',
    title: 'City Unwrapped',
    kicker: 'Art · 2026',
    summary: 'Scanning myself and my environment and passing control.',
    href: '/art?project=city-unwrapped',
    frames: projectFrames.city,
    ...fromSketch(
      scaleSketch([[120, 230], [270, 165], [340, 190], [470, 320], [455, 500], [420, 580], [150, 610], [70, 470], [150, 340]], 0.78),
      -1.8,
      { threads: [[220, 205], [330, 220]] },
    ),
  },
  {
    id: 'sand-remembers',
    title: 'The Sand Remembers the Waves',
    kicker: 'Art · 2025',
    summary: 'Oil-based monotype relief prints on newsprint.',
    href: '/art?project=sand-remembers',
    frames: projectFrames.sand,
    ...fromSketch(
      scaleSketch([[560, 330], [780, 220], [1045, 400], [820, 610], [710, 930], [600, 440]], 0.8),
      -0.8,
      { threads: [[670, 300], [760, 270]] },
    ),
  },
  {
    id: 'my-little-chum',
    title: 'My Little Chum',
    kicker: 'Animation · 2026',
    summary: 'Animated music video for Teddy Mailer\'s song "My Little Chum".',
    href: '/animation?project=my-little-chum',
    frames: projectFrames.chum,
    ...fromSketch(
      scaleSketch([[1420, 330], [1725, 330], [1740, 410], [1715, 540], [1620, 630], [1420, 640]], 0.8),
      -4.6,
      { threads: [[1480, 360], [1655, 360]] },
    ),
  },
  {
    id: 'cell-migration',
    title: 'Cell Migration Simulation',
    kicker: 'Code · 2024',
    summary: 'A Python simulation exploring two possible mechanisms of chemotaxis',
    href: '/code?project=cell-migration',
    frames: projectFrames.code,
    ...fromSketch(
      scaleSketch([[1200, 390], [1390, 540], [1370, 805], [1325, 960], [1065, 940], [960, 790], [950, 670]], 0.8),
      -3.1,
      { threads: [[1150, 470], [1275, 460]] },
    ),
  },
  {
    id: 'lavender',
    title: 'The Lavender Magazine Issue 16',
    kicker: 'Design · 2025',
    summary: 'Magazine cover design centered on ritual, texture, and atmosphere.',
    href: '/design?project=lavender',
    frames: projectFrames.design,
    ...fromSketch(
      scaleSketch([[90, 740], [390, 690], [510, 950], [180, 1330]], 0.82),
      0.1,
      { threads: [[220, 745], [370, 720]] },
    ),
  },
  {
    id: 'paper-plane',
    title: 'Paper Plane',
    kicker: 'Animation · 2025',
    summary: 'An animated short about childhood dreams.',
    href: '/animation?project=paper-plane',
    frames: framesFor('paper-study', 'flying', 1, 65),
    ...fromSketch(
      scaleSketch([[430, 1085], [610, 790], [770, 885], [835, 1045], [785, 1205], [655, 1345], [505, 1290], [390, 1120], [385, 1025]], 0.78),
      1.8,
      { threads: [[500, 960], [630, 940]] },
    ),
  },
  {
    id: 'will-fridays',
    title: 'Will Fridays',
    kicker: 'Process · 2026',
    summary: 'I made like a game that you can play every friday and you can win \'will\' rewards',
    href: '/code?project=will-fridays',
    frames: projectFrames.will,
    ...fromSketch(
      scaleSketch([[970, 800], [1210, 765], [1335, 920], [1270, 1260], [1010, 1300], [890, 1015]], 0.72),
      1.2,
      { threads: [[1025, 830], [1170, 815]] },
    ),
  },
  {
    id: 'and-they-danced',
    title: 'And They Danced',
    kicker: 'Animation · 2025',
    summary: 'A vibrant animated piece capturing the joy of dance.',
    href: '/animation?project=and-they-danced',
    frames: projectFrames.fish,
    ...fromSketch(
      scaleSketch([[1650, 560], [1770, 560], [1860, 930], [1740, 1085], [1645, 1230], [1430, 1290], [1365, 1170], [1430, 1090], [1540, 1005], [1600, 875], [1645, 710]], 0.78),
      -1.1,
      { threads: [[1690, 600], [1750, 600]] },
    ),
  },
]

export default projects
